import { ReactNode, useState, useEffect } from "react";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { Button } from "@/components/ui/button";
import { SessionTracker } from "@/components/SessionTracker";
import { LogOut, ArrowLeft, Users, Menu, AlertTriangle, X, Brain } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AiCreditBadge } from "@/components/ai-credits/AiCreditBadge";
import { WhatsAppNotificationsButton } from "@/components/layouts/WhatsAppNotificationsButton";
import { useBranding } from "@/hooks/useBranding";
import { AnnouncementDialog } from "@/components/announcements/AnnouncementDialog";
import { canShowAnnouncementOn } from "@/lib/announcements";

interface SimplifiedWorkspaceLayoutProps {
    children: ReactNode;
}

// Hoisted to module scope so the array reference is stable across renders.
// Previously this was rebuilt on every render, defeating React's prop equality
// checks for any memoized children that received it.
const BASE_NAVIGATION = [
    { name: "Campaigns", href: "/campaigns" },
    { name: "LinkedIn Accounts", href: "/linkedin-accounts" },
    { name: "Profiles", href: "/profiles" },
    { name: "CRM", href: "/crm" },
    { name: "Inbox", href: "/inbox", isNew: false },
] as const;

export const SimplifiedWorkspaceLayout = ({ children }: SimplifiedWorkspaceLayoutProps) => {
    const { workspace, isImpersonating, isSuperAdmin, isActuallyPartnerAdmin, isActuallyStaff } = useWorkspace();
    const { signOut, user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const branding = useBranding();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [bannerDismissed, setBannerDismissed] = useState(false);

    // Partner-managed workspaces hide AI Credits + WhatsApp opt-in and force
    // the regular /inbox (no Leads CRM) regardless of workspace.settings.
    const isPartnerManaged = !!(workspace as any)?.partner_id;

    // Prefetch heavy route chunks on idle so clicking Leads / AI Knowledge
    // feels instant (no Suspense spinner while the JS chunk downloads).
    const aiEnabled = true;
    useEffect(() => {
      const w = window as any;
      const schedule = w.requestIdleCallback || ((cb: any) => setTimeout(cb, 800));
      const cancel = w.cancelIdleCallback || clearTimeout;
      const handle = schedule(() => {
        if (aiEnabled) import("@/pages/CompanyKnowledge").catch(() => {});
      });
      return () => cancel(handle);
    }, [aiEnabled]);

    const { data: subscription, isSuccess: subscriptionLoaded } = useQuery({
        queryKey: ["subscription-expiry-banner", workspace?.id],
        queryFn: async () => {
            const { data } = await supabase
                .from("account_subscriptions" as any)
                .select("current_period_end")
                .eq("workspace_id", workspace!.id)
                .eq("status", "active")
                .not("current_period_end", "is", null)
                .order("current_period_end", { ascending: true })
                .limit(1)
                .maybeSingle();
            return data;
        },
        enabled: !!workspace?.id && !isSuperAdmin,
    });

    const daysLeft = (subscription as any)?.current_period_end
        ? Math.ceil((new Date((subscription as any).current_period_end).getTime() - Date.now()) / (1000 * 3600 * 24))
        : null;

    // Managed workspaces show per-account banners instead of the workspace-wide one.
    const isManagedBilling = (workspace as any)?.settings?.simplified_onboarding_enabled === true;
    const showExpiryBanner = !isManagedBilling && !bannerDismissed && daysLeft !== null && daysLeft <= 7 && !isSuperAdmin;
    const isUrgent = daysLeft !== null && daysLeft <= 3;
    // Reserve vertical space for the banner area while the subscription
    // query is still loading so content doesn't jump down on first paint.
    const showBannerPlaceholder = !isManagedBilling && !isSuperAdmin && !!workspace?.id && !subscriptionLoaded;

    const showBackButton = location.pathname.startsWith('/inbox') || location.pathname.startsWith('/linkedin-accounts');

    const handleExitImpersonation = () => {
        localStorage.removeItem('super_admin_impersonated_workspace');
        localStorage.removeItem('partner_admin_impersonated_workspace');
        localStorage.removeItem('staff_impersonated_workspace');
        const partnerReturnTo = localStorage.getItem('partner_admin_return_to');
        localStorage.removeItem('partner_admin_return_to');
        toast.success("Exited workspace access");
        if (isActuallyStaff) {
            window.location.href = "/admin/organizations";
        } else if (isActuallyPartnerAdmin) {
            window.location.href = partnerReturnTo || "/partner/workspaces";
        } else {
            window.location.href = "/admin/organizations";
        }
    };

    const navigation = BASE_NAVIGATION;

    return (
        <div className="min-h-screen flex flex-col bg-background">
            <SessionTracker />
            {workspace?.id && !isImpersonating && !isSuperAdmin && !isActuallyStaff && canShowAnnouncementOn(location.pathname) && <AnnouncementDialog workspaceId={workspace.id} />}
            {isImpersonating && (
                <div className="bg-amber-500 text-amber-950 px-4 py-2 flex items-center justify-between z-[60] relative">
                    <div className="flex items-center gap-2">
                        <span className="text-xs sm:text-sm font-medium truncate">
                            👀 {isActuallyStaff ? "Staff" : isActuallyPartnerAdmin ? "White-label" : "Super Admin"} viewing: {workspace?.name}
                        </span>
                    </div>
                    <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={handleExitImpersonation}
                        className="h-7 text-amber-950 hover:bg-amber-600 shrink-0"
                    >
                        <LogOut className="w-4 h-4 sm:mr-2" />
                        <span className="hidden sm:inline">Exit View Mode</span>
                    </Button>
                </div>
            )}

            {/* Header */}
            <header className="h-14 sm:h-16 border-b border-border bg-background/80 backdrop-blur-sm px-4 sm:px-8 flex items-center justify-between sticky top-0 z-30">
                {/* Logo & Workspace Name */}
                <div 
                    className="flex items-center gap-2 min-w-0 cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => navigate('/')}
                >
                    {branding.isPartnerBranded && branding.logoLight && branding.logoLight !== "/logo.png" ? (
                        <img
                            src={branding.logoLight}
                            alt={branding.productName}
                            className="h-7 sm:h-8 w-auto max-w-[140px] object-contain shrink-0"
                        />
                    ) : (
                        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-primary flex items-center justify-center shrink-0 shadow-sm">
                            <span className="text-primary-foreground font-bold text-base sm:text-lg">
                                {(workspace?.name || branding.productName || "W").charAt(0).toUpperCase()}
                            </span>
                        </div>
                    )}
                    <span className="font-semibold text-sm sm:text-lg tracking-tight truncate max-w-[120px] sm:max-w-none">
                        {workspace?.name || "Workspace"}
                    </span>
                </div>

                {/* Desktop Navigation */}
                <div className="hidden md:flex items-center gap-3">
                    {!isPartnerManaged && workspace?.id && <AiCreditBadge workspaceId={workspace.id} />}
                    {!isPartnerManaged && <WhatsAppNotificationsButton />}
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate("/linkedin-accounts")}
                        className="text-muted-foreground hover:text-foreground"
                    >
                        <Users className="w-4 h-4 mr-2" />
                        LinkedIn Accounts
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate("/profiles")}
                        className="text-muted-foreground hover:text-foreground"
                    >
                        <Brain className="w-4 h-4 mr-2" />
                        Profiles
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => signOut()}
                        className="text-muted-foreground hover:text-foreground"
                    >
                        <LogOut className="w-4 h-4 mr-2" />
                        Sign Out
                    </Button>
                </div>

                {/* Mobile Menu */}
                <div className="md:hidden">
                    <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                        <SheetTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-9 w-9">
                                <Menu className="w-5 h-5" />
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="right" className="w-72 p-0">
                            <div className="flex flex-col h-full">
                                <div className="p-4 border-b border-border">
                                    <h2 className="font-semibold">{workspace?.name || "Workspace"}</h2>
                                    {!isPartnerManaged && workspace?.id && <div className="mt-2"><AiCreditBadge workspaceId={workspace.id} /></div>}
                                </div>
                                <nav className="flex-1 p-4 space-y-1">
                                    {navigation.map((item) => (
                                        <Link
                                            key={item.name}
                                            to={item.href}
                                            onClick={() => setMobileMenuOpen(false)}
                                            className={cn(
                                                "flex items-center justify-between gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                                                location.pathname === item.href
                                                    ? "bg-primary/10 text-primary"
                                                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                                            )}
                                        >
                                            <span>{item.name}</span>
                                            {(item as any).isNew && (
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-primary/10 text-primary border border-primary/20">
                                                    New
                                                </span>
                                            )}
                                        </Link>
                                    ))}
                                </nav>
                                <div className="p-4 border-t border-border space-y-3">
                                    <Button
                                        variant="ghost"
                                        className="w-full justify-start text-muted-foreground"
                                        onClick={() => {
                                            setMobileMenuOpen(false);
                                            signOut();
                                        }}
                                    >
                                        <LogOut className="w-4 h-4 mr-2" />
                                        Sign Out
                                    </Button>
                                </div>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </header>

            {/* Subscription Expiry Warning Banner — reserve space while loading to prevent layout shift */}
            {showExpiryBanner ? (
                <div className={cn(
                    "px-4 sm:px-8 py-2.5 flex items-center justify-between border-b",
                    isUrgent
                        ? "bg-destructive/10 border-destructive/20 text-destructive"
                        : "bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-200"
                )}>
                    <div className="flex items-center gap-2 text-sm font-medium">
                        <AlertTriangle className="w-4 h-4 shrink-0" />
                        <span>
                            {daysLeft === 0
                                ? "Your subscription expires today!"
                                : daysLeft === 1
                                    ? "Your subscription expires tomorrow!"
                                    : `Your subscription expires in ${daysLeft} days.`}
                        </span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <Button
                            size="sm"
                            variant={isUrgent ? "destructive" : "default"}
                            className="h-7 text-xs"
                            onClick={() => {
                                window.location.href = `https://dashboard.wdctoday.com/renew/${workspace?.slug}`;
                            }}
                        >
                            Renew Now
                        </Button>
                        <button
                            onClick={() => setBannerDismissed(true)}
                            className="p-1 rounded-md hover:bg-black/5 transition-colors"
                        >
                            <X className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </div>
            ) : showBannerPlaceholder ? (
                // Invisible placeholder matching banner height — prevents content
                // from jumping down once the subscription query resolves.
                <div aria-hidden className="h-0" />
            ) : null}

            {/* Main Content */}
            <main className="flex-1 p-4 md:p-8 overflow-auto bg-muted/10">
                <div className={location.pathname.startsWith("/crm") ? "w-full" : "max-w-5xl mx-auto"}>
                    {showBackButton && (
                        <div className="mb-4 sm:mb-6">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="pl-0 hover:pl-2 transition-all gap-2 text-muted-foreground hover:text-foreground"
                                onClick={() => navigate('/campaigns')}
                            >
                                <ArrowLeft className="w-4 h-4" />
                                Back to Dashboard
                            </Button>
                        </div>
                    )}
                    {children}
                </div>
            </main>
        </div>
    );
};